import React, { useState } from "react";
import {
  Typography,
  Card,
  CardHeader,
  CardContent,
  Box,
  useTheme,
  useMediaQuery,
  Divider,
  Alert,
  Snackbar,
} from "@mui/material";
import { styled } from "@mui/system";
import { Upload } from "lucide-react";
import DropZone from "./DropZone";

const IconWrapper = styled("span")(({ theme }) => ({
  marginRight: theme.spacing(1),
  display: "inline-flex",
  alignItems: "center",
}));

interface ImportSectionProps {
  onImportClick: (file?: File) => void;
  onContractImportClick: (files?: File[]) => void;
}

const ImportSection: React.FC<ImportSectionProps> = ({
  onImportClick,
  onContractImportClick,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [error, setError] = useState<string | null>(null);

  const Excel_Max_FILE_SIZE = 6 * 1024 * 1024;
  const MAX_FILE_SIZE = 3 * 1024 * 1024;

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const validateFileSize = (
    files: File[],
    maxSize: number
  ): { valid: boolean; error?: string } => {
    const oversizedFiles = files.filter((file) => file.size > maxSize);

    if (oversizedFiles.length > 0) {
      const fileList = oversizedFiles
        .map((f) => `${f.name} (${formatFileSize(f.size)})`)
        .join(", ");
      return {
        valid: false,
        error: `The following files exceed the 6 MB limit: ${fileList}. Maximum file size allowed is 6 MB.`,
      };
    }

    return { valid: true };
  };

  const handleImportClick = (files?: File[]) => {
    setError(null);

    if (files && files.length > 0) {
      const validation = validateFileSize(files, Excel_Max_FILE_SIZE);
      if (!validation.valid) {
        setError(validation.error || "File size validation failed");
        return;
      }
      onImportClick(files[0]);
    } else {
      onImportClick();
    }
  };

  const handleContractImportClick = (files?: File[]) => {
    setError(null);

    if (files && files.length > 0) {
      const validation = validateFileSize(files, MAX_FILE_SIZE);
      if (!validation.valid) {
        setError(validation.error || "File size validation failed");
        return;
      }
      onContractImportClick(files);
    } else {
      onContractImportClick();
    }
  };

  const handleCloseError = () => {
    setError(null);
  };

  return (
    <>
      <Card
        elevation={0}
        sx={{ height: "100%", border: "1px solid", borderColor: "divider" }}
      >
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconWrapper>
                <Upload size={20} color={theme.palette.primary.main} />
              </IconWrapper>
              <Typography variant="h6">Import Transactions</Typography>
            </Box>
          }
          subheader="Import transaction data from Excel spreadsheets or contracts (Max 6 MB per file)"
          sx={{ pb: 1 }}
        />
        <Divider />
        <CardContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
            }}
          >
            <DropZone
              onImportClick={handleImportClick}
              allowDrop={false}
              header={"Import Excel or CSV file"}
              subHeader={
                "Upload Excel or CSV file. CSV files can be prepared in Excel, Google Sheets, Numbers, and other applications – Max 6 MB"
              }
              children={
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" fontSize={"0.8rem"}>
                      Please follow the step-by-step instructions and download
                      the Imports Template provided on the Resources page.
                    </Typography>
                  </Alert>
                </>
              }
              buttonText={"Import Excel"}
              icon="spreadsheet"
              maxFileSize={Excel_Max_FILE_SIZE}
            />
            <DropZone
              allowDrop={false}
              onImportClick={handleContractImportClick}
              header={"Import HUD/ALTA/CD"}
              subHeader={
                "Upload HUD/ALTA/CD documents (PDF or images) - Max 3 MB each"
              }
              buttonText={"Import HUD/ALTA/CD"}
              icon="document"
              maxFileSize={MAX_FILE_SIZE}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseError}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ImportSection;
